import type { ApiResponse, CookieLoginRequest, CookieLoginResponse, User } from "types";

import { getError, instance, SUCCESS_STATUS_CODE } from "./client";

const STORAGE_KEYS = {
  USER: "session_user",
} as const;

export const setUser = (user: User | null): void => {
  if (user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEYS.USER);
  }
};

export const getUser = (): User | null => {
  const userStr = localStorage.getItem(STORAGE_KEYS.USER);
  if (!userStr) {
    return null;
  }

  try {
    return JSON.parse(userStr) as User;
  } catch {
    // If parsing fails, remove corrupted data
    localStorage.removeItem(STORAGE_KEYS.USER);
    return null;
  }
};

export const isAuthenticated = (): boolean => Boolean(getUser());

export const login = async (
  username: string,
  password: string,
  twoFactorAuthToken?: string,
): Promise<ApiResponse<User>> => {
  try {
    const requestBody: CookieLoginRequest = {
      username,
      password,
    };

    if (twoFactorAuthToken) {
      requestBody.token = twoFactorAuthToken;
    }

    const loginResponse = await instance.post("/auth/login/", requestBody);

    const loginData: CookieLoginResponse = loginResponse.data;

    if (
      loginResponse.status === SUCCESS_STATUS_CODE &&
      loginData &&
      loginData.active_two_factor &&
      !loginData.authenticated
    ) {
      return [null, "2FA_REQUIRED"];
    }

    if (loginData.user === null) {
      return [null, "INVALID_USER"];
    }

    const user: User = {
      ...loginData,
      id: loginData.user_id ?? 0,
      email: loginData.user_email ?? "",
      username: loginData.user,
      name: loginData.first_name || loginData.user,
      url: "",
      accessToken: "",
    };
    setUser(user);
    return [user, null];
  } catch (error) {
    return [null, getError(error)];
  }
};

export const logout = async (): Promise<[boolean, string]> => {
  try {
    const logoutResponse = await instance.get("/auth/logout/");
    if (logoutResponse.status !== SUCCESS_STATUS_CODE) {
      return [false, "Could not log out"];
    }
    setUser(null);
    return [true, ""];
  } catch (error) {
    return [false, getError(error)];
  }
};

/**
 * Initializes the auth state on app startup
 */
export const initAuth = async (): Promise<void> => {
  try {
    // Start by making an empty login GET request to see if the user is authenticated.
    const loginResponse = await instance.get("/auth/login/");
    const loginData: CookieLoginResponse = loginResponse.data;

    if (!loginData.authenticated || loginData.user === null) {
      setUser(null);
      return;
    }

    // Interceptor in API client automatically redirects on unauthorized response.
    const user: User = {
      ...loginData,
      id: loginData.user_id ?? 0,
      email: loginData.user_email ?? "",
      username: loginData.user,
      name: loginData.first_name || loginData.user,
      url: "",
      accessToken: "",
    };
    setUser(user);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // If the request fails (likely unauthorized), ensure we clear any stale user.
    setUser(null);
  }
};
